import React from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { msalConfig } from './authConfig'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import ProjectEconomyWrappers from './components/ProjectEconomyWrappers/ProjectEconomyWrappers'
import SystemSettings from './components/SystemSettings/SystemSettings'
import { RouteNotFound } from './components/SystemState/States/RouteNotFound'
import ResultCalculatedProjects from './components/Reports/GridReports/ResultCalculatedProjects/ResultCalculatedProjects'
import { ResultCalculationReport } from './components/Reports/PdfReports/Reports/ResultCalculationReport'
import { FinalAccountReport } from './components/Reports/PdfReports/Reports/FinalAccountReport'
import ForecastResultCalculation from './components/Reports/GridReports/ForecastResultCalculation/ForecastResultCalculation'
import SectionAccess from './AccessControl/SectionAccess'
import { ProfitOnContractReport } from './components/Reports/PdfReports/Reports/ProfitOnContractReport'
import { FluentProvider, webLightTheme } from '@fluentui/react-components'
import ProjektprognosWrappers from './components/Projektprognos/ProjektprognosWrappers'

// Suppress benign ResizeObserver errors från AG Grid
// Detta är ett känt problem när många rader expanderas/kollapsas samtidigt
// Felet påverkar inte funktionaliteten - det är bara ResizeObserver som inte hinner
// rapportera alla storleksändringar innan nästa frame och är patchat i AG Grid 30+ men
// utan en uppdaterad licens får vi dölja felet på detta sätt
const resizeObserverErrHandler = (e: ErrorEvent) => {
  if (
    e.message ===
      'ResizeObserver loop completed with undelivered notifications.' ||
    e.message === 'ResizeObserver loop limit exceeded'
  ) {
    const resizeObserverErr = document.getElementById(
      'webpack-dev-server-client-overlay'
    )
    if (resizeObserverErr) {
      resizeObserverErr.style.display = 'none'
    }
    e.stopImmediatePropagation()
  }
}
window.addEventListener('error', resizeObserverErrHandler)

// if (process.env.NODE_ENV === 'development') {
//   const { worker } = require('./mocks/browser')
//   worker.start()
// }

const msalInstance = new PublicClientApplication(msalConfig)

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <RouteNotFound />,
    children: [
      {
        path: 'projekt',
        children: [
          {
            index: true,
            element: <RouteNotFound />,
          },
          {
            path: ':projectId',
            element: (
              <SectionAccess scopeKey="sheet_read">
                <ProjectEconomyWrappers />
              </SectionAccess>
            ),
          },
        ],
      },
      {
        path: 'installningar',
        element: (
          <SectionAccess
            scopeKey={[
              'account_templates',
              'scheduling_crud',
              'accounting_read',
              'accounting_create',
              'system_settings',
            ]}
          >
            <SystemSettings />
          </SectionAccess>
        ),
      },
      {
        path: 'rapport',
        children: [
          {
            index: true,
            element: <RouteNotFound />,
          },
          {
            path: 'resultatavrakning/:projectId',
            element: (
              <SectionAccess scopeKey="report_read">
                <ResultCalculationReport />
              </SectionAccess>
            ),
          },
          {
            path: 'slutredovisning/:projectId',
            element: (
              <SectionAccess scopeKey="report_read">
                <FinalAccountReport />
              </SectionAccess>
            ),
          },
          {
            path: 'entreprenadVinst/:projectId',
            element: (
              <SectionAccess scopeKey="report_read">
                <ProfitOnContractReport />
              </SectionAccess>
            ),
          },
          {
            path: 'projektprognos/:projectId',
            element: (
              <SectionAccess scopeKey="report_read">
                <ProjektprognosWrappers />
              </SectionAccess>
            ),
          },
          {
            path: 'resultatavraknadeProjekt',
            element: (
              <SectionAccess scopeKey="accounting_read">
                <ResultCalculatedProjects />
              </SectionAccess>
            ),
          },
          {
            path: 'prognosResultatavrakning',
            element: (
              <SectionAccess scopeKey="accounting_read">
                <ForecastResultCalculation />
              </SectionAccess>
            ),
          },
        ],
      },
    ],
  },
])

const container = document.getElementById('root')
if (!container) throw new Error('Failed to find the root element')
const root = createRoot(container)

root.render(
  <React.StrictMode>
    <FluentProvider theme={webLightTheme}>
      <MsalProvider instance={msalInstance}>
        <RouterProvider router={router} />
      </MsalProvider>
    </FluentProvider>
  </React.StrictMode>
)
